import { memo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TableSortLabel,
  Tooltip,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { visuallyHidden } from "@mui/utils";
import amazonLogo from "../../assets/svgs/amazon.svg";
import { numberFormatter } from "../../helper/Reports/common";

function MarketingStreamTable({
  dataHeads = [],
  dataBody = [],
  isLoading = false,
  onSortClick,
  orderByData = [["ams_sp_traffic.hour", "asc"]],
  summaryData = {},
  noDataFoundMessage = "No Data Found",
  groupByOptionsData = [],
  onExtraGroupByChange = () => {},
  showGroupByPlusIcon = false,
}) {
  const [orderBy, setOrderBy] = useState(orderByData?.[0]?.[0] || "");
  const [order, setOrder] = useState(orderByData?.[0]?.[1] || "asc");
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const nextOrder = isAsc ? "desc" : "asc";
    setOrder(nextOrder);
    setOrderBy(property);
    onSortClick?.(property, nextOrder);
  };

  const handlePlusClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (groupByOption) => {
    const dimensions = groupByOption?.dimensions || [];
    onExtraGroupByChange?.(dimensions, "add");
    handleMenuClose();
  };

  const handleRemoveExtraColumnClick = (key) => {
    onExtraGroupByChange?.([key], "remove");
  };

  if (dataBody.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "250px",
          color: "text.secondary",
          gap: 1.5,
        }}
      >
        <SearchOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
        <Typography variant="subtitle1">{noDataFoundMessage}</Typography>
      </Box>
    );
  }

  const stickyLeftStyle = (left = 0, bgColor = "#ffffff") => ({
    position: "sticky",
    left,
    zIndex: 3,
    background: bgColor,
    borderRight: "1px solid #ddd",
    color: "#00000099",
    fontWeight: 600,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  });

  const commonCellStyle = (isActive, bgColor = "#F7F6FE") => ({
    background: isActive ? bgColor : "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
    padding: "6px 16px",
    borderRight: "1px solid #ddd",
    minWidth: 120,
  });

  const zIndexes = {
    header: 5,
    summary: 4,
    stickyColumn: 6,
  };

  const dimensionColumns = dataHeads.filter((col) => col?.isDimension);
  const lastDimensionColumnKey =
    dimensionColumns?.[dimensionColumns.length - 1]?.key || "";

  const HEADER_MAX_LENGTH = 22;
  const getTruncatedHeaderText = (headerText = "") => {
    const normalizedText = String(headerText);
    if (normalizedText.length <= HEADER_MAX_LENGTH) return normalizedText;
    return `${normalizedText.slice(0, HEADER_MAX_LENGTH)}...`;
  };

  const renderFormattedValue = (col, value) => {
    if (value === undefined || value === null || value === "") return "-";
    if (col.isDimension) return value;
    return numberFormatter(
      value,
      !!col.isCurrencyFormat,
      col?.format,
      col?.maximumFractionDigits ?? 2
    );
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "auto",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "none",
        marginTop: 2,
      }}
    >
      <TableContainer sx={{ maxHeight: 600, minHeight: 400 }}>
        <Table stickyHeader size="small" sx={{ borderCollapse: "separate" }}>
          <TableHead>
            <TableRow
              sx={{
                zIndex: zIndexes.header,
                position: "sticky",
                top: 0,
                height: 56,
              }}
            >
              {dataHeads.map((col) =>
                col.isSticky ? (
                  <TableCell
                    key={`${col.key}-header-sticky`}
                    sx={{
                      ...stickyLeftStyle(
                        col.stickyLeft,
                        orderBy === col.key ? "#F7F6FE" : "#fff"
                      ),
                      padding: "14px 16px",
                      minWidth: col.minWidth || 120,
                      zIndex: zIndexes.stickyColumn,
                      textAlign: "left",
                      borderBottom: "none",
                      cursor: "pointer",
                      position: "sticky",
                    }}
                    onClick={() => handleSort(col.key)}
                  >
                    <TableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : "asc"}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          color: "#00000099 !important",
                        },
                      }}
                    >
                      <Tooltip
                        title={col.shortTitle || ""}
                        arrow
                        placement="top"
                        disableHoverListener={
                          String(col.shortTitle || "").length <=
                          HEADER_MAX_LENGTH
                        }
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#00000099",
                          }}
                        >
                          {getTruncatedHeaderText(col.shortTitle)}
                        </Typography>
                      </Tooltip>
                    </TableSortLabel>

                    {col.key === lastDimensionColumnKey &&
                      showGroupByPlusIcon && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlusClick(e);
                          }}
                          sx={{
                            position: "absolute",
                            right: -12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 24,
                            height: 24,
                            backgroundColor: "#7558ea",
                            color: "white",
                            zIndex: zIndexes.stickyColumn + 2,
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                            "&:hover": {
                              backgroundColor: "#5a42c7",
                            },
                            padding: 0,
                          }}
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}

                    {col.isRemovable && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveExtraColumnClick(col.key);
                        }}
                        sx={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 24,
                          height: 24,
                          zIndex: zIndexes.stickyColumn + 2,
                          padding: 0,
                        }}
                      >
                        <CancelIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </TableCell>
                ) : (
                  <TableCell
                    key={`${col.key}-header`}
                    align={col.alignment || "right"}
                    sx={{
                      ...commonCellStyle(orderBy === col.key),
                      borderBottom: "none",
                      zIndex: zIndexes.header,
                      minWidth: col.minWidth || 120,
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1}
                      alignItems={col.alignment || "flex-end"}
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleSort(col.key)}
                    >
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : "asc"}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                          flexDirection: "row-reverse",
                          justifyContent: "space-between",
                        }}
                      >
                        <img
                          src={amazonLogo}
                          alt="platform"
                          width={14}
                          height={14}
                          style={{ display: "block" }}
                        />
                        {orderBy === col.key && (
                          <Box sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        )}
                      </TableSortLabel>
                      <Tooltip
                        title={col.shortTitle || ""}
                        arrow
                        placement="top"
                        disableHoverListener={
                          String(col.shortTitle || "").length <=
                          HEADER_MAX_LENGTH
                        }
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#00000099",
                            textAlign: "right",
                          }}
                        >
                          {getTruncatedHeaderText(col.shortTitle)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )
              )}
            </TableRow>

            <TableRow
              sx={{
                position: "sticky",
                top: 56,
                zIndex: zIndexes.summary,
                backgroundColor: "#f9f9f9",
              }}
            >
              {dataHeads.map((col, colIndex) =>
                col.isSticky ? (
                  <TableCell
                    key={`summary-${col.key}`}
                    sx={{
                      ...stickyLeftStyle(
                        col.stickyLeft,
                        orderBy === col.key ? "#EAE6FD" : "#f9f9f9"
                      ),
                      padding: "14px 16px",
                      minWidth: col.minWidth || 120,
                      zIndex: zIndexes.stickyColumn,
                      borderTop: "1px solid #ddd",
                      fontWeight: 600,
                    }}
                  >
                    {colIndex === 0 ? "Summary" : ""}
                  </TableCell>
                ) : (
                  <TableCell
                    key={`summary-${col.key}`}
                    align={col.alignment || "right"}
                    sx={{
                      fontSize: "0.75rem",
                      padding: "14px 16px",
                      background: orderBy === col.key ? "#EAE6FD" : "#f9f9f9",
                      borderRight: "1px solid #ddd",
                      minWidth: col.minWidth || 120,
                      fontWeight: 600,
                      zIndex: zIndexes.summary,
                      borderTop: "1px solid #ddd",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isLoading
                      ? "-"
                      : renderFormattedValue(col, summaryData?.[col.key])}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? [...Array(10)].map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    {dataHeads.map((col, colIdx) => (
                      <TableCell
                        key={`skeleton-${idx}-${col.key}-${colIdx}`}
                        align={col.alignment || "right"}
                        sx={{
                          position: col.isSticky ? "sticky" : "static",
                          left: col.isSticky ? col.stickyLeft || 0 : "auto",
                          background: "white",
                          zIndex: col.isSticky ? 2 : 1,
                          minWidth: col.minWidth || 120,
                          borderRight: "1px solid #ddd",
                          padding: "14px 16px",
                          fontSize: "0.75rem",
                        }}
                      >
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : dataBody.map((row, rowIdx) => (
                  <TableRow
                    key={`row-${rowIdx}`}
                    hover
                    sx={{
                      "&:hover td": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {dataHeads.map((col) => {
                      const value = row?.[col.key];
                      return (
                        <TableCell
                          key={`row-${rowIdx}-${col.key}`}
                          align={col.alignment || "right"}
                          sx={{
                            position: col.isSticky ? "sticky" : "unset",
                            left: col.isSticky ? col.stickyLeft || 0 : "auto",
                            background:
                              orderBy === col.key ? "#F7F6FE" : "white",
                            zIndex: col.isSticky ? 2 : 1,
                            minWidth: col.minWidth || 120,
                            borderRight: "1px solid #ddd",
                            padding: "14px 16px",
                            fontSize: "0.75rem",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: col.isDimension ? 600 : 500,
                              textAlign: col.isDimension ? "left" : "right",
                            }}
                          >
                            {renderFormattedValue(col, value)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            maxWidth: 200,
            maxHeight: 400,
            mt: 1,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {groupByOptionsData?.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleMenuItemClick(option)}
            sx={{
              fontSize: "12px",
              textWrap: "wrap",
              wordBreak: "break-word",
              padding: "8px 16px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
}

export default memo(MarketingStreamTable);
